import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/layout/Layout";

// Eager-loaded (critical navigation path)
import LandingPage  from "@/components/LandingPage";
import Login        from "@/components/auth/Login";
import Register     from "@/components/auth/Register";
import ForgotPassword from "@/components/auth/ForgotPassword";
import ResetPassword  from "@/components/auth/ResetPassword";
import SubscriptionExpired from "@/components/auth/SubscriptionExpired";
import Onboarding   from "@/components/auth/Onboarding";
import PricingPage  from "@/components/PricingPage";
import Dashboard    from "@/components/dashboard/Dashboard";
import RadarView    from "@/components/radar/RadarView";
import ProcessDetail from "@/components/process/ProcessDetail";

// Lazy-loaded secondary pages (reduces initial bundle)
const FavoritesList  = lazy(() => import("@/components/favorites/FavoritesList"));
const AlertsCenter   = lazy(() => import("@/components/alerts/AlertsCenter"));
const CompanyProfile = lazy(() => import("@/components/profile/CompanyProfile"));
const UserManagement = lazy(() => import("@/components/admin/UserManagement"));
const SystemConfig   = lazy(() => import("@/components/admin/SystemConfig"));
const SuperAdminPanel = lazy(() => import("@/components/admin/SuperAdminPanel"));
const HealthCheck    = lazy(() => import("@/components/health/HealthCheck"));
const DocumentManager = lazy(() => import("@/components/documents/DocumentManager"));
const ExperienceLibrary = lazy(() => import("@/components/experiences/ExperienceLibrary"));
const ProcessDossier = lazy(() => import("@/components/dossier/ProcessDossier"));
const SubscriptionManager = lazy(() => import("@/components/billing/SubscriptionManager"));

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function isSubscriptionActive(user) {
  if (!user) return false;
  const status = user.subscription_status || "special";
  if (status === "special" || status === "active") return true;
  if (status === "trial" && user.trial_ends_at) {
    return new Date(user.trial_ends_at) > new Date();
  }
  return false;
}

function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#080C14] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/" replace />;
  if (!isSubscriptionActive(user)) return <Navigate to="/suscripcion" replace />;
  if (superAdminOnly && user.role !== "SUPER_ADMIN") return <Navigate to="/dashboard" replace />;
  if (adminOnly && !["ADMIN", "SUPER_ADMIN"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/precios" element={<PricingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/suscripcion" element={<SubscriptionExpired />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/radar" element={<ProtectedRoute><Layout><RadarView /></Layout></ProtectedRoute>} />
        <Route path="/procesos/:id" element={<ProtectedRoute><Layout><ProcessDetail /></Layout></ProtectedRoute>} />
        <Route path="/favoritos" element={<ProtectedRoute><Layout><FavoritesList /></Layout></ProtectedRoute>} />
        <Route path="/alertas" element={<ProtectedRoute><Layout><AlertsCenter /></Layout></ProtectedRoute>} />
        <Route path="/perfil" element={<ProtectedRoute><Layout><CompanyProfile /></Layout></ProtectedRoute>} />
        <Route path="/documentos" element={<ProtectedRoute><Layout><DocumentManager /></Layout></ProtectedRoute>} />
        <Route path="/experiencias" element={<ProtectedRoute><Layout><ExperienceLibrary /></Layout></ProtectedRoute>} />
        <Route path="/expediente/:id" element={<ProtectedRoute><Layout><ProcessDossier /></Layout></ProtectedRoute>} />
        <Route path="/admin/usuarios" element={<ProtectedRoute adminOnly><Layout><UserManagement /></Layout></ProtectedRoute>} />
        <Route path="/admin/config" element={<ProtectedRoute adminOnly><Layout><SystemConfig /></Layout></ProtectedRoute>} />
        <Route path="/admin/super" element={<ProtectedRoute superAdminOnly><Layout><SuperAdminPanel /></Layout></ProtectedRoute>} />
        {/* Health Check: visible only for ADMIN and SUPER_ADMIN (enforced also in Sidebar) */}
        <Route path="/health" element={<ProtectedRoute adminOnly><Layout><HealthCheck /></Layout></ProtectedRoute>} />
        <Route path="/mi-suscripcion" element={<ProtectedRoute><SubscriptionManager /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
      });
    }
  }, []);

  return (
    <div className="App">
      <Toaster position="top-right" richColors theme="dark" />
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
